import type { components } from "@oathera/contract/api";
import type { StatusLevel } from "../../components/index.ts";
import type {
  PostureByCodeAndTier,
  PostureDriftEntry,
  PostureFindingPage,
} from "./use-posture.ts";

/**
 * Presentation vocabulary for the Posture screen (task 11.10; design 12.2 Posture row, 12.3/12.4
 * principles). It keeps the one-way maps from the generated contract shapes onto the screen's
 * presentational pieces in a single, testable place and defines NO API shapes (those come from
 * `@oathera/contract/api` via `use-posture.ts`).
 *
 * THE RISK TIER (design 12.4: "risk tier conveyed by icon+text, not colour alone"; higher tiers
 * surfaced prominently). The posture catalog `enum_risk_tier` is `low | medium | high | critical |
 * other`. The screen must NEVER rely on colour alone to tell those apart, and must make the higher
 * tiers stand out. Three independent, non-colour channels do that here:
 *   1. a stable SEVERITY RANK ({@link riskTierRank}) that orders tiers so critical/high sort to the
 *      top of every table and the by-code-and-tier grid;
 *   2. a {@link StatusLevel} ({@link riskTierStatus}) that drives a `StatusBadge` — an ICON + TEXT
 *      label — so each tier reads as a glyph and a word, with colour only ever a redundant fourth
 *      channel. critical/high map to `error`, medium to `warn`, low to `neutral`, and the catalog
 *      `other` to grey `unknown` (never green — an unknown tier is not reassuring, design 12.3);
 *   3. a {@link riskTierProminent} flag that the components use to add a redundant non-colour
 *      emphasis (a left rule / weight) to the highest tiers, so prominence does not depend on hue.
 *
 * The finding code (`enum_finding_code`) and the posture kind (`enum_posture_kind`, nhi|host) get
 * human labels and a stable catalog order so the by-code-and-tier breakdown reads consistently. The
 * host-drift enums (`enum_host_change_kind`, `enum_host_confidence`, `enum_host_attestation`) get
 * labels too; `host_confidence`/`host_attestation` are nullable per contract and the `null` is shown
 * honestly as "Unknown"/"Unreported" rather than invented. The maps here are the single place those
 * contract enums become `StatusLevel`s, human labels, catalog orders and `/events` drill links.
 */

type PostureKind = components["schemas"]["enum_posture_kind"];
type FindingCode = components["schemas"]["enum_finding_code"];
type RiskTier = components["schemas"]["enum_risk_tier"];
type HostChangeKind = components["schemas"]["enum_host_change_kind"];
type HostConfidence = components["schemas"]["enum_host_confidence"];
type HostAttestation = components["schemas"]["enum_host_attestation"];

/* ---- Catalog orders (the frozen presentation order) ----------------------------------------- */

/**
 * Risk tiers from HIGHEST severity to lowest, `other` last. This is the presentation order the
 * by-code-and-tier grid's tier columns and the tier sort read; it puts critical/high first so the
 * most serious findings are surfaced prominently (design 12.2). Mirrors `enum_risk_tier`.
 */
export const RISK_TIER_ORDER: readonly RiskTier[] = [
  "critical",
  "high",
  "medium",
  "low",
  "other",
];

/**
 * Finding codes in a stable presentation order (NHI identity codes first, then host codes, `other`
 * last). The by-code-and-tier breakdown orders its rows by this so the table reads consistently
 * regardless of the order the server returned aggregates in. Mirrors `enum_finding_code`.
 */
export const FINDING_CODE_ORDER: readonly FindingCode[] = [
  "improper_offboarding",
  "overprivileged",
  "long_lived_credentials",
  "stale_identity",
  "unowned_identity",
  "host_changed",
  "host_low_confidence",
  "host_unattested",
  "other",
];

/* ---- Risk tier: the non-colour conveyance (design 12.4) ------------------------------------- */

/**
 * Severity rank for a risk tier: higher is more severe. Drives sorting so critical/high surface to
 * the top (design 12.2). `other` ranks LOWEST (−1) deliberately: an uncategorised tier is not more
 * severe than a known low tier, and should not jump ahead of real findings.
 */
export function riskTierRank(tier: RiskTier): number {
  switch (tier) {
    case "critical":
      return 4;
    case "high":
      return 3;
    case "medium":
      return 2;
    case "low":
      return 1;
    case "other":
      return -1;
  }
}

/**
 * Map a risk tier onto a status level for the icon+text `StatusBadge` (design 12.4). critical and
 * high are `error`; medium is `warn`; low is `neutral` (a real-but-lowest tier, not a "good" green);
 * the catalog `other` is grey `unknown` — an uncategorised tier is never shown as healthy/green
 * (design 12.3). No tier ever maps to `healthy`: a posture FINDING is never a reassuring green.
 */
export function riskTierStatus(tier: RiskTier): StatusLevel {
  switch (tier) {
    case "critical":
    case "high":
      return "error";
    case "medium":
      return "warn";
    case "low":
      return "neutral";
    case "other":
      return "unknown";
  }
}

/** Human label for a risk tier. */
export function riskTierLabel(tier: RiskTier): string {
  switch (tier) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    case "other":
      return "Other";
  }
}

/** A longer accessible phrase for a risk tier, used as the badge's `title`. */
export function riskTierTitle(tier: RiskTier): string {
  return `Risk tier: ${riskTierLabel(tier).toLowerCase()}.`;
}

/**
 * Whether a tier should be rendered PROMINENTLY (critical or high). The components use this to add a
 * redundant, non-colour emphasis (a left rule / heavier weight) so the most serious tiers stand out
 * without relying on hue (design 12.2 "higher tiers surfaced prominently"; 12.4 "not colour alone").
 */
export function riskTierProminent(tier: RiskTier): boolean {
  return tier === "critical" || tier === "high";
}

/* ---- Posture kind & finding code labels ----------------------------------------------------- */

/** Human label for a posture kind (`nhi` non-human identity, `host` host configuration). */
export function postureKindLabel(kind: PostureKind): string {
  return kind === "nhi" ? "Non-human identity" : "Host";
}

/** Human label for a finding code. */
export function findingCodeLabel(code: FindingCode): string {
  switch (code) {
    case "improper_offboarding":
      return "Improper offboarding";
    case "overprivileged":
      return "Overprivileged";
    case "long_lived_credentials":
      return "Long-lived credentials";
    case "stale_identity":
      return "Stale identity";
    case "unowned_identity":
      return "Unowned identity";
    case "host_changed":
      return "Host changed";
    case "host_low_confidence":
      return "Host low confidence";
    case "host_unattested":
      return "Host unattested";
    case "other":
      return "Other";
  }
}

/**
 * The posture KIND a finding code belongs to. The catalog's three `host_*` codes are host findings;
 * every other code is an NHI (non-human identity) finding. Used to label the by-code-and-tier
 * breakdown rows by kind without inventing a field (the aggregate row carries only code + tier).
 */
export function findingCodeKind(code: FindingCode): PostureKind {
  switch (code) {
    case "host_changed":
    case "host_low_confidence":
    case "host_unattested":
      return "host";
    default:
      return "nhi";
  }
}

/* ---- Host-drift enums ------------------------------------------------------------------------ */

/** Human label for what changed about a host (`enum_host_change_kind`). */
export function hostChangeKindLabel(kind: HostChangeKind): string {
  switch (kind) {
    case "fingerprint":
      return "Fingerprint changed";
    case "confidence":
      return "Confidence changed";
    case "attestation":
      return "Attestation changed";
    case "other":
      return "Other change";
  }
}

/**
 * Human label for a host confidence, null-safe. `null` (the contract allows it) is shown as
 * "Unreported" so a missing reading is never mistaken for the catalog `unknown` value — they are
 * different facts (no reading vs an explicitly-unknown reading).
 */
export function hostConfidenceLabel(confidence: HostConfidence | null): string {
  if (confidence === null) return "Unreported";
  switch (confidence) {
    case "unknown":
      return "Unknown";
    case "low":
      return "Low";
    case "medium":
      return "Medium";
    case "high":
      return "High";
    case "other":
      return "Other";
  }
}

/**
 * Map a host confidence onto a status level for icon+text display (design 12.4). `high` is the
 * strongest (but still only `neutral` — a drift entry is not a reassuring green); `medium`/`low`/
 * `unknown` are `warn`; the catalog `other` and a `null` (unreported) are grey `unknown` — never
 * green (design 12.3).
 */
export function hostConfidenceStatus(confidence: HostConfidence | null): StatusLevel {
  if (confidence === null) return "unknown";
  switch (confidence) {
    case "high":
      return "neutral";
    case "medium":
    case "low":
      return "warn";
    case "unknown":
    case "other":
      return "unknown";
  }
}

/**
 * Human label for a host attestation strength, null-safe. `null` is "Unreported" (distinct from the
 * catalog `none`, which is an explicit "no attestation").
 */
export function hostAttestationLabel(attestation: HostAttestation | null): string {
  if (attestation === null) return "Unreported";
  switch (attestation) {
    case "none":
      return "None";
    case "software":
      return "Software";
    case "hardware":
      return "Hardware";
    case "other":
      return "Other";
  }
}

/**
 * Map a host attestation strength onto a status level for icon+text display (design 12.4).
 * `hardware` is the strongest (but only `neutral`); `software` is `warn`; an explicit `none` is an
 * `error` (no attestation at all); the catalog `other` and a `null` (unreported) are grey `unknown`.
 */
export function hostAttestationStatus(attestation: HostAttestation | null): StatusLevel {
  if (attestation === null) return "unknown";
  switch (attestation) {
    case "hardware":
      return "neutral";
    case "software":
      return "warn";
    case "none":
      return "error";
    case "other":
      return "unknown";
  }
}

/* ---- By-code-and-tier derivation ------------------------------------------------------------- */

/** One rendered by-code-and-tier row: the code, its posture kind and one count per tier column. */
export interface ByCodeRow {
  readonly code: FindingCode;
  readonly kind: PostureKind;
  /** The count per tier, in {@link RISK_TIER_ORDER}; absent tiers are zero. */
  readonly countsByTier: Readonly<Record<RiskTier, number>>;
  /** The row's total across all tiers. */
  readonly total: number;
  /** The most severe tier that has a non-zero count on this row (for prominence/sort), or null. */
  readonly worstTier: RiskTier | null;
}

/** A zeroed per-tier count map used as the base for every row. */
function zeroTierCounts(): Record<RiskTier, number> {
  return { critical: 0, high: 0, medium: 0, low: 0, other: 0 };
}

/**
 * Reconcile the server's `by_code_and_tier` aggregates into one row per OBSERVED finding code, each
 * carrying a count for every tier column (zero when the server reported none for that code/tier).
 * Rows are ordered first by their worst non-zero tier (most severe first, so critical/high surface
 * prominently — design 12.2), then by the catalog code order, then by total. This does NOT invent
 * rows for codes the server did not report (unlike the enforcement always-present breakdowns, the
 * posture aggregate is observed-only — a code with no findings in the window simply has no row), so
 * an empty window shows the table's honest empty state rather than a wall of fabricated zeros.
 */
export function deriveByCodeRows(aggregates: readonly PostureByCodeAndTier[]): readonly ByCodeRow[] {
  const byCode = new Map<FindingCode, Record<RiskTier, number>>();
  for (const agg of aggregates) {
    const counts = byCode.get(agg.code) ?? zeroTierCounts();
    counts[agg.risk_tier] += agg.count;
    byCode.set(agg.code, counts);
  }

  const rows: ByCodeRow[] = [];
  for (const [code, countsByTier] of byCode) {
    let total = 0;
    let worstTier: RiskTier | null = null;
    for (const tier of RISK_TIER_ORDER) {
      const c = countsByTier[tier];
      total += c;
      if (c > 0 && (worstTier === null || riskTierRank(tier) > riskTierRank(worstTier))) {
        worstTier = tier;
      }
    }
    rows.push({ code, kind: findingCodeKind(code), countsByTier, total, worstTier });
  }

  const codeRank = (c: FindingCode): number => FINDING_CODE_ORDER.indexOf(c);
  return rows.sort((a, b) => {
    const aw = a.worstTier === null ? -Infinity : riskTierRank(a.worstTier);
    const bw = b.worstTier === null ? -Infinity : riskTierRank(b.worstTier);
    if (aw !== bw) return bw - aw; // most severe worst-tier first
    const cr = codeRank(a.code) - codeRank(b.code);
    if (cr !== 0) return cr;
    return b.total - a.total;
  });
}

/* ---- Trend (the honest-unknown surfacing) --------------------------------------------------- */

/**
 * Whether the posture contract provides a FINDINGS-over-time series. It does not: `PostureFindingPage`
 * carries only the point-in-time `by_code_and_tier` counts and `items`, with NO trend array (only
 * `PostureDriftPage` has a `trend`). This constant makes that contract gap explicit so the screen
 * surfaces a findings trend as an honest "unknown — no series in the contract" note rather than
 * fabricating one from the snapshot counts (design 12.3). The DRIFT trend DOES exist and is charted.
 */
export const FINDINGS_TREND_AVAILABLE = false as const;

/* ---- Emptiness ------------------------------------------------------------------------------- */

/** True when there are no findings AND no by-code-and-tier aggregates to show. */
export function isFindingsEmpty(page: PostureFindingPage): boolean {
  return page.items.length === 0 && page.by_code_and_tier.length === 0;
}

/** True when there are no host-drift entries to show. */
export function isDriftEmpty(entries: readonly PostureDriftEntry[]): boolean {
  return entries.length === 0;
}

/* ---- Drill-down to /events ------------------------------------------------------------------- */

/**
 * Build a relative `/events` link from a set of filter params. Values are encoded by
 * `URLSearchParams`; empty values are dropped. The posture endpoints attach no server-built `drill`
 * (the responses carry no `drill` field), so the screen composes honest standing filters from real
 * `/events` params — never an invented route — consistent with the enforcement/integrity screens.
 * The events screen (task 11.12) reads these params.
 */
function eventsHref(params: Readonly<Record<string, string>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.length > 0) search.set(key, value);
  }
  const qs = search.toString();
  return qs.length > 0 ? `/events?${qs}` : "/events";
}

/**
 * The `/events` filter for a finding code at a specific risk tier (the by-code-and-tier drill).
 * `category=posture` plus the code and risk tier are faithful standing filters, so each code/tier
 * cell drills to exactly the posture events behind it (design 12.3).
 */
export function byCodeTierHref(code: FindingCode, tier: RiskTier): string {
  return eventsHref({ category: "posture", code, risk_tier: tier });
}

/** The `/events` filter for ALL findings of a code, across tiers (the row-total drill). */
export function byCodeHref(code: FindingCode): string {
  return eventsHref({ category: "posture", code });
}

/**
 * The `/events` filter for a single NHI/host finding. Carries the finding's code, risk tier and
 * source, plus the agent when the finding has one (the contract allows a null agent).
 */
export function findingHref(finding: {
  code: FindingCode;
  risk_tier: RiskTier;
  source_id: string;
  agent: string | null;
}): string {
  const base: Record<string, string> = {
    category: "posture",
    code: finding.code,
    risk_tier: finding.risk_tier,
    source_id: finding.source_id,
  };
  if (finding.agent !== null) base["agent"] = finding.agent;
  return eventsHref(base);
}

/**
 * The `/events` filter for a host-drift alert. The drift entry carries its own `event_id`, so the
 * drill targets that exact event where possible; the source and change kind are included as faithful
 * standing filters too. Each drift alert therefore drills straight to its event (design 12.3).
 */
export function driftHref(entry: PostureDriftEntry): string {
  return eventsHref({
    category: "host_drift",
    event_id: entry.event_id,
    source_id: entry.source_id,
    change: entry.change_kind,
  });
}
